import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useBriefsArchive } from '@/lib/queries';
import { fmtMonthDay, fmtYear } from '@/lib/format';

export default function BriefsArchive() {
  const { data, isLoading, error } = useBriefsArchive(100);

  return (
    <>
      <Card>
        <CardTitle>Briefs Archive</CardTitle>
        <CardDescription className="mt-2">
          Historical daily briefs. Click any date for the full executive summary, themes, and cited sources.
        </CardDescription>
      </Card>

      {isLoading && <LoadingSkeleton lines={8} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && data.length === 0 && (
        <Card>
          <CardDescription>
            No briefs yet. Trigger "daily-brief" from Debug → Triggers to generate the first one.
          </CardDescription>
        </Card>
      )}

      {data && data.length > 0 && (
        <Card>
          <p className="text-muted text-xs uppercase tracking-widest mb-4 font-(family-name:--font-ui)">
            {data.length} briefs
          </p>
          <ul className="flex flex-col">
            {data.map((b) => (
              <li key={b.briefDate}>
                <Link
                  to={`/hmhwatch/briefs/${b.briefDate}`}
                  className="grid grid-cols-[72px_1fr_24px] md:grid-cols-[90px_1fr_24px] gap-3 md:gap-4 items-center py-4 border-b border-border last:border-0 hover:bg-surface-hover -mx-2 px-2 rounded-(--radius-sm) min-h-20"
                >
                  <div className="text-center border-r border-border pr-3">
                    <div className="font-(family-name:--font-heading) text-lg md:text-xl font-semibold">
                      {fmtMonthDay(b.briefDate)}
                    </div>
                    <div className="text-muted text-[0.7rem] tracking-widest font-(family-name:--font-ui)">
                      {fmtYear(b.briefDate)}
                    </div>
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    {b.preview && (
                      <p className="font-(family-name:--font-body) text-sm leading-snug text-text mb-2 line-clamp-2">
                        {b.preview}
                      </p>
                    )}
                    <div className="text-[0.7rem] text-muted font-(family-name:--font-ui) tracking-widest uppercase flex flex-wrap gap-x-2">
                      {b.totalItems > 0 && <span>{b.totalItems} items scored</span>}
                      {b.themeCount > 0 && <span>· {b.themeCount} themes</span>}
                      {b.pushed && <span className="text-accent">· pushed</span>}
                      <span>· {b.modelVersion}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
